// A document that starts on an overlay URL (a reload, a deep link, iOS restoring a suspended PWA
// on the card it was left on) has no live history entry underneath: "back" — the close button or
// the swipe gesture — would load the previous entry as a new document, a white screen and a full
// boot. Before the router starts, the entries the overlay sits on are written into the history
// of this document, so back always stays in-document and the list is already mounted underneath.
//
// The states follow @tanstack/history (`key`, `__TSR_key`, `__TSR_index`), so the router sees
// a BACK of one step, not a GO of unknown length.

const STATE_INDEX_KEY = '__TSR_index';

// Cards opened from a card's related words are pushed, so back returns to the previous card;
// the depth of that chain travels in the history state, and the close button steps over the
// whole chain to land on the list (`useCloseOverlay`). A seeded history (below) has no chain.
declare module '@tanstack/react-router' {
  interface HistoryState {
    chain?: number;
  }
}

// The paths under an overlay path, top to bottom, without the overlay itself. Empty when the
// path is not an overlay (or the parent is unknown).
export function overlayParents(pathname: string, lastDictionaryId: string | null = null): string[] {
  const dict = /^\/d\/([^/]+)(\/.*)?$/.exec(pathname);
  if (dict) {
    const [, id, rest = ''] = dict;
    const list = `/d/${id}`;
    if (rest === '' || rest === '/') return [];
    if (/^\/(search|add|settings)\/?$/.test(rest)) return [list];
    // the preview of a related word sits on the list like a card does
    if (/^\/l\/[^/]+\/?$/.test(rest)) return [list];
    const word = /^\/w\/([^/]+)(\/edit)?\/?$/.exec(rest);
    if (word) return word[2] ? [list, `${list}/w/${word[1]}`] : [list];
    return [];
  }
  if (/^\/(settings|dictionaries\/new)\/?$/.test(pathname)) return lastDictionaryId ? [`/d/${lastDictionaryId}`] : [];
  return [];
}

// Whether the path opens on top of another screen; such a navigation leaves the page under it
// where it was scrolled (`scrollRestoration` in main.tsx)
export function isOverlay(pathname: string): boolean {
  return overlayParents(pathname, '-').length > 0;
}

function randomKey(): string {
  return (Math.random() + 1).toString(36).substring(7);
}

function state(index: number): Record<string, unknown> {
  const key = randomKey();
  return { key, __TSR_key: key, [STATE_INDEX_KEY]: index };
}

// Rewrites the current history entry as the first parent and pushes the rest and the overlay
// URL on top. Returns the parents written, empty when nothing was done.
export function seedOverlayHistory(win: Window, lastDictionaryId: string | null = null): string[] {
  const { pathname, search, hash } = win.location;
  const parents = overlayParents(pathname, lastDictionaryId);
  if (parents.length === 0) return [];
  try {
    win.history.replaceState(state(0), '', parents[0]);
    for (let i = 1; i < parents.length; i++) win.history.pushState(state(i), '', parents[i]);
    win.history.pushState(state(parents.length), '', `${pathname}${search}${hash}`);
  } catch {
    // history is read-only here (a sandboxed frame): the overlay still closes via the fallback
  }
  return parents;
}
